// Motor de Calificación, Auditoría de Sinceridad y Veredicto Oficial UNINAV 2026
// Implementa las fórmulas psicométricas, escalas de validez (Lie Scale),
// pares de consistencia cruzada y el dictamen de admisión SEMAR (Apto / No Apto).

import { CONSISTENCY_PAIRS } from './personalityInventory';

/**
 * Calificación de la Batería 1: Psicométrica (Raven / Beta-III / Bennett / Wonderlic)
 */
export function calculatePsychometricEvaluation(questions, userAnswers) {
  let correctCount = 0;
  const breakdown = {};

  questions.forEach((q, idx) => {
    const isCorrect = userAnswers[idx] === q.correct_index;
    if (isCorrect) correctCount++;

    const cat = q.category || 'General';
    if (!breakdown[cat]) {
      breakdown[cat] = { correct: 0, total: 0 };
    }
    breakdown[cat].total += 1;
    if (isCorrect) breakdown[cat].correct += 1;
  });

  const total = questions.length || 1;
  const percentage = Math.round((correctCount / total) * 100);

  // Estimación de CI Naval Estandarizado (Media 100, Desviación Estándar 15)
  // Rango estándar: 80 a 130
  const estimatedCI = Math.round(85 + (correctCount / total) * 45);

  // Estimación de Percentil poblacional (1 a 99)
  let percentile = 50;
  if (estimatedCI < 85) percentile = Math.max(5, Math.round((estimatedCI / 85) * 20));
  else if (estimatedCI <= 100) percentile = Math.round(20 + ((estimatedCI - 85) / 15) * 30);
  else if (estimatedCI <= 115) percentile = Math.round(50 + ((estimatedCI - 100) / 15) * 35);
  else percentile = Math.min(99, Math.round(85 + ((estimatedCI - 115) / 15) * 14));

  const isPassing = estimatedCI >= 90;

  return {
    correctCount,
    totalQuestions: total,
    percentage,
    estimatedCI,
    percentile,
    isPassing,
    breakdown
  };
}

/**
 * Calificación de la Batería 2: Inventario de Personalidad y Escalas de Sinceridad
 */
export function calculatePersonalityEvaluation(questions, userAnswers) {
  let lieTriggers = 0;
  let totalLieQuestions = 0;
  const dimensionScores = {};

  questions.forEach((q) => {
    const ans = userAnswers[q.id]; // 'V' o 'F'
    if (q.lieScale) {
      totalLieQuestions++;
      if (ans === 'V') lieTriggers++;
    }

    const dim = q.dimension;
    if (!dimensionScores[dim]) {
      dimensionScores[dim] = { points: 0, maxPoints: 0 };
    }

    if (q.direction === 'POSITIVE') {
      dimensionScores[dim].maxPoints += 1;
      if (ans === 'V') dimensionScores[dim].points += 1;
    } else if (q.direction === 'NEGATIVE') {
      dimensionScores[dim].maxPoints += 1;
      if (ans === 'F') dimensionScores[dim].points += 1;
    } else if (q.direction === 'ADMITTED_NORMAL') {
      dimensionScores[dim].maxPoints += 1;
      if (ans === 'V') dimensionScores[dim].points += 1; // Admite vulnerabilidad normal
    }
  });

  // Evaluación de Pares de Consistencia Cruzada
  let contradictions = 0;
  const contradictionDetails = [];

  CONSISTENCY_PAIRS.forEach((pair) => {
    const ansA = userAnswers[pair.itemA];
    const ansB = userAnswers[pair.itemB];

    if (ansA && ansB) {
      const expA = pair.expectedConsistent.itemA;
      const expB = pair.expectedConsistent.itemB;

      // Se considera contradicción si una de las respuestas es contraria al patrón de coherencia interna
      const isContradiction = (ansA === expA && ansB !== expB) || (ansA !== expA && ansB === expB);
      if (isContradiction) {
        contradictions++;
        contradictionDetails.push({
          pairId: pair.id,
          concept: pair.concept,
          responseA: ansA,
          responseB: ansB
        });
      }
    }
  });

  const totalPairs = CONSISTENCY_PAIRS.length;
  const consistencyPercentage = totalPairs > 0 
    ? Math.max(0, Math.round(((totalPairs - contradictions) / totalPairs) * 100))
    : 100;

  // Criterios estrictos de validez psicométrica SEMAR:
  // - Si lieTriggers >= 3: Deseabilidad social patológica (fingimiento / mentira evidente)
  // - Si contradictions >= 2: Inconsistencia y contradicciones graves entre ítems espejo
  const hasLieFlag = lieTriggers >= 3;
  const hasInconsistencyFlag = contradictions >= 2;
  const isProfileValid = !hasLieFlag && !hasInconsistencyFlag;

  let validityStatus = "Prueba Válida y Coherente";
  let validityColor = "#34d399";
  let validitySummary = "Tus respuestas reflejan honestidad, congruencia y madurez psicológica sin intentos detectados de falseamiento.";

  if (hasLieFlag && hasInconsistencyFlag) {
    validityStatus = "Inválida: Alta Deseabilidad Social e Inconsistencia Severa";
    validityColor = "#ef4444";
    validitySummary = "Se detectó intento de proyectar una imagen irrealmente perfecta aunado a múltiples contradicciones en reactivos redundantes. Causa directa de 'No Apto' en SEMAR.";
  } else if (hasLieFlag) {
    validityStatus = "Alerta: Deseabilidad Social Detectada (Escala L Elevada)";
    validityColor = "#f59e0b";
    validitySummary = "Intentaste responder lo que 'creíste que querían escuchar' afirmando no tener ningún defecto o emoción humana normal. El psicólogo naval te interrogará exhaustivamente al respecto.";
  } else if (hasInconsistencyFlag) {
    validityStatus = "Alerta: Inconsistencia Interna (Contradicciones Cruzadas)";
    validityColor = "#ef4444";
    validitySummary = "Respondiste de forma contradictoria a preguntas idénticas formuladas de diferente manera, denotando falta de atención o manipulación.";
  }

  return {
    lieTriggers,
    totalLieQuestions,
    contradictions,
    totalPairs,
    consistencyPercentage,
    isProfileValid,
    hasLieFlag,
    hasInconsistencyFlag,
    validityStatus,
    validityColor,
    validitySummary,
    contradictionDetails,
    dimensionScores
  };
}

/**
 * Calificación de la Batería 3: Test MOSS de Juicio Situacional y Liderazgo
 */
export function calculateMossEvaluation(questions, userAnswers) {
  let totalScore = 0;
  const maxPossible = questions.length * 4;

  const dimensionPerformance = {};

  questions.forEach((q) => {
    const selectedOptionIdx = userAnswers[q.id];
    if (selectedOptionIdx !== undefined && q.options[selectedOptionIdx]) {
      const opt = q.options[selectedOptionIdx];
      const pts = opt.score || 0;
      totalScore += pts;

      const dim = q.dimension || 'Juicio General';
      if (!dimensionPerformance[dim]) {
        dimensionPerformance[dim] = { points: 0, maxPoints: 0 };
      }
      dimensionPerformance[dim].points += pts;
      dimensionPerformance[dim].maxPoints += 4;
    }
  });

  const percentage = maxPossible > 0 ? Math.round((totalScore / maxPossible) * 100) : 0;
  const isPassing = percentage >= 65;

  return {
    totalScore,
    maxPossible,
    percentage,
    isPassing,
    dimensionPerformance
  };
}

/**
 * Generación del Dictamen Global UNINAV 2026 (Apto vs No Apto)
 */
export function generateComprehensiveVerdict({ psychometric, personality, moss }) {
  // 1. Verificación de descarte por Escalas de Sinceridad e Inconsistencia (Causa #1 SEMAR)
  if (personality && (!personality.isProfileValid || personality.hasInconsistencyFlag)) {
    return {
      verdict: "NO APTO",
      badgeText: "🔴 NO APTO - INCONSISTENCIA EN ESCALAS DE VALIDEZ",
      color: "#ef4444",
      primaryReason: "Falta de consistencia y congruencia en las pruebas psicológicas o detección de falseamiento (Escala de Sinceridad).",
      advice: "En la Universidad Naval, un aspirante que finge respuestas o cae en contradicciones es catalogado como no confiable para el mando militar. La honestidad y el autoconocimiento son indispensables.",
      canAccessInterviewGuide: true
    };
  }

  // 2. Verificación de descarte por CI Psicométrico
  if (psychometric && !psychometric.isPassing) {
    return {
      verdict: "NO APTO",
      badgeText: "🔴 NO APTO - UMBRAL COGNITIVO INSUFICIENTE",
      color: "#ef4444",
      primaryReason: `CI Naval estimado (${psychometric.estimatedCI}) inferior al límite mínimo requerido (CI 90) para el Heroico Colegio Naval o Medicina Naval.`,
      advice: "Entrena con mayor rigor los ejercicios de lógica deductiva, matrices abstractas y poleas/engranes cronometrados.",
      canAccessInterviewGuide: true
    };
  }

  // 3. Verificación de liderazgo / juicio MOSS
  if (moss && !moss.isPassing) {
    return {
      verdict: "EN OBSERVACIÓN",
      badgeText: "🟡 EN OBSERVACIÓN - TACTO Y MANDO A REFORZAR",
      color: "#f59e0b",
      primaryReason: "Buen desempeño cognitivo y sinceridad, pero tu criterio de liderazgo y resolución de conflictos interpersonales requiere mayor madurez.",
      advice: "Enfócate en respuestas que demuestren firmeza sin autoritarismo desmedido y alto apego a la justicia y camaradería militar.",
      canAccessInterviewGuide: true
    };
  }

  // 4. Si supera todos los filtros con éxito:
  return {
    verdict: "APTO",
    badgeText: "🟢 APTO PARA LA FASE DEFINITIVA (SEMAR)",
    color: "#10b981",
    primaryReason: "Has demostrado solidez cognitiva (CI >= 90), alta congruencia psicológica sin falseamiento y excelente juicio de liderazgo militar.",
    advice: "Mantén este nivel de serenidad, honestidad y postura firme para la entrevista cara a cara con el oficial psicólogo en Antón Lizardo, Veracruz.",
    canAccessInterviewGuide: true
  };
}
